import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingController, MenuController } from '@ionic/angular';
import { LanguageService } from 'src/app/services/language.service';
import { UserService } from 'src/app/services/user.service';
import { FontsizeService } from 'src/app/services/fontsize.service';
import { Subscription } from 'rxjs';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { TranslationService } from '../../services/translation.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { ImageService } from 'src/app/services/image.service';
import { Organization } from 'src/app/models/organization';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
})
export class SideMenuComponent implements OnInit, OnDestroy {
  public appPages = [
    { title: 'nav-home', url: 'home', icon: 'home', section: 'primary' },
    { title: 'nav-guide', url: 'guide', icon: 'explore', section: 'primary' },
    {
      title: 'nav-services',
      url: 'services',
      icon: 'store',
      section: 'primary',
    },
    {
      title: 'nav-profile',
      url: 'profile',
      icon: 'insert_emoticon',
      section: 'primary',
    },
    // ---------------- //
    {
      title: 'nav-privacy-policy',
      url: 'privacy-policy',
      icon: 'info',
      section: 'secondary',
    },
    {
      title: 'nav-accessibility-report',
      url: 'accessibility-report',
      icon: 'info',
      section: 'secondary',
    },
    {
      title: 'nav-cookie-declaration',
      url: 'cookie-declaration',
      icon: 'info',
      section: 'secondary',
    },
    {
      title: 'nav-send-feedback',
      url: 'send-feedback',
      icon: 'info',
      section: 'secondary',
      external: true,
    },
    // ---------------- //
    {
      title: 'nav-in-cooperation',
      url: 'in-cooperation',
      icon: 'info',
      section: 'tertiary',
    },
  ];

  languages: string[] = [];
  currentLanguage = '';
  popoverOptions: any = {
    cssClass: 'language-selector',
  };
  currentFontSize: number;
  langSubscription: Subscription;
  public logoUrl: string | undefined = undefined;
  public selectedOrganization: Organization;
  public organizationDetails = '';
  public appInstanceName = '';

  private organizationSubscription: Subscription;

  constructor(
    private userService: UserService,
    private languageService: LanguageService,
    private router: Router,
    private menu: MenuController,
    private fontSizeService: FontsizeService,
    private translationService: TranslationService,
    private loadingController: LoadingController,
    private gtmService: GoogleTagManagerService,
    private organizationService: OrganizationService,
    private image: ImageService,
  ) {
    this.languages = this.languageService.getLanguages();
    this.generateAppPages();
    this.organizationSubscription = this.userService.selectedOrganizationObservable.subscribe(
      (organization) => {
        this.updateOrganization(organization.id);
        this.loadBranding(organization.id);
      },
    );
  }

  generateAppPages = () => {
    const appPages = [];
    appPages.push(
      ...[
        { title: 'nav-home', url: 'home', icon: 'home', section: 'primary' },
        {
          title: 'nav-guide',
          url: 'guide',
          icon: 'explore',
          section: 'primary',
        },
      ],
    );
    appPages.push({
      title: 'nav-services',
      url: 'services',
      icon: 'store',
      section: 'primary',
    });
    appPages.push(
      ...[
        {
          title: 'nav-profile',
          url: 'profile',
          icon: 'insert_emoticon',
          section: 'primary',
        },
        // ---------------- //
        {
          title: 'nav-privacy-policy',
          url: 'privacy-policy',
          icon: 'info',
          section: 'secondary',
        },
        {
          title: 'nav-accessibility-report',
          url: 'accessibility-report',
          icon: 'info',
          section: 'secondary',
        },
        {
          title: 'nav-cookie-declaration',
          url: 'cookie-declaration',
          icon: 'info',
          section: 'secondary',
        },
        {
          title: 'nav-send-feedback',
          url: 'feedback',
          icon: 'info',
          section: 'secondary',
        },
        // ---------------- //
        {
          title: 'nav-in-cooperation',
          url: 'in-cooperation',
          icon: 'info',
          section: 'tertiary',
        },
      ],
    );
    this.appPages = appPages;
  };

  accessabilityKeys() {
    window.addEventListener('keyup', (e) => {
      if (e.keyCode === 27 || e.key === '27') {
        //close menu with esc
        this.menu.close();
      }
    });
  }

  ngOnInit() {
    if (this.userService.user.appInstance && this.userService.user.appInstance.translations) {
      this.appInstanceName = this.translationService.getCorrectTranslation(
        this.userService.user.appInstance,
      ).name;
    }
    const organizationId = this.userService.user.organizationId;
    this.currentFontSize = this.checkSavedFont();
    this.accessabilityKeys();
    this.setCurrentLanguage();
    this.generateAppPages();
    this.updateOrganization(organizationId);
  }

  ngOnDestroy() {
    // FIX: Menu component is not destroyed on close so unsubsribe will never run
    this.organizationSubscription.unsubscribe();
  }

  IncreaseFontSize() {
    if (this.currentFontSize + 2 > 24) {
      return;
    }
    this.currentFontSize = this.currentFontSize + 2;
    this.fontSizeService.setFontSize(this.currentFontSize);
  }

  DecreaseFontSize() {
    if (this.currentFontSize - 2 < 16) {
      return;
    }
    this.currentFontSize = this.currentFontSize - 2;
    this.fontSizeService.setFontSize(this.currentFontSize);
  }

  setCurrentLanguage() {
    this.currentLanguage = this.languageService.getLanguage();
    this.generateAppPages();
  }

  async changeAppLanguage($event) {
    if (!$event || !$event.target || !$event.target.value) {
      return;
    }
    const newLanguage = $event.target.value;

    const loadingElement = await this.loadingController.create();
    await loadingElement.present();

    try {
      await this.translationService.initTranslations();
    } catch (error) {}

    loadingElement.dismiss();

    this.gtmService.pushTag({
      event: 'languageSelected',
      languageChoice: newLanguage,
    });

    this.languageService.setLanguage(newLanguage);
    this.setCurrentLanguage();

    if (this.userService.user) {
      this.userService.user.setLanguage(newLanguage);
    }

    const path = window.location.pathname.split('/');
    if (path.length > 2) {
      const newUrl = window.location.pathname.split('/');
      const query = window.location.search;
      newUrl[1] = newLanguage.split('-')[0];
      this.router.navigateByUrl(newUrl.join('/') + query, {
        replaceUrl: true,
      });

      this.menu.close();
    }
  }

  // changeAppLanguage = ($event) => {
  //   const language = $event.target.value;
  //   this.languageService.setLanguage(language);
  //   this.setCurrentLanguage();
  //   if (this.userService.user) {
  //     this.userService.user.setLanguage(language);
  //   }
  //   this.gtmService.pushTag({
  //     event: 'languageSelected',
  //     language,
  //   });
  //   const newUrl = this.router.url.split('/');
  //   newUrl[1] = language;
  //   this.router.navigateByUrl(newUrl.join('/'));
  // };

  navigateToPage = (page: string) => {
    const newUrl = this.router.url.split('/').splice(0, 4);
    newUrl[3] = page;
    console.log(newUrl.join('/'));
    this.router.navigateByUrl(newUrl.join('/'), { replaceUrl: true });
  };

  navigateToRootPage = (page: string) => {
    this.router.navigate([this.currentLanguage, page]);
  };

  navigateToExternal = (url: string) => {
    window.location.href = url;
  };

  sectionPages(section: string) {
    return this.appPages.filter((page) => page.section === section);
  }

  navigateToAreaSelector() {
    const lang = this.userService.user.language;
    this.router.navigate([lang, 'area-selector'], { queryParams: { skipLogin: true } });
  }

  async updateOrganization(orgId: string) {
    if (!orgId) {
      // this.feedbackformUrl = undefined;
      return;
    }

    const organization = await this.organizationService.getById(orgId);

    if (!organization) {
      // this.feedbackformUrl = undefined;
      return;
    }
    // const translation = this.translationService.getCorrectTranslation(organization);
    // this.feedbackformUrl = translation.feedback_form;
    this.selectedOrganization = organization;
    this.generateOrganizationDetails();
  }

  generateOrganizationDetails() {
    this.organizationDetails = '';
    if (this.selectedOrganization.details && this.selectedOrganization.details.municipality) {
      this.organizationDetails += this.selectedOrganization.details.municipality + ' ';
    }

    if (this.selectedOrganization.details && this.selectedOrganization.details.country) {
      this.organizationDetails += this.selectedOrganization.details.country;
    }

    if (
      this.selectedOrganization.details &&
      this.selectedOrganization.details.municipality &&
      this.selectedOrganization.details.municipality.length > 0 &&
      this.selectedOrganization.details &&
      this.selectedOrganization.details.country &&
      this.selectedOrganization.details.country.length > 0
    ) {
      this.organizationDetails =
        this.selectedOrganization.details.municipality +
        ', ' +
        this.selectedOrganization.details.country;
    }
  }

  loadBranding = (orgId: string) => {
    this.organizationService.getBranding(orgId).then((branding) => {
      const imageId = branding?.logo;
      if (imageId) {
        const imageUrl = this.image.getUrlById(imageId);
        this.logoUrl = imageUrl;
      } else {
        this.logoUrl = '../../assets/icons/ml-icon-192.png';
      }
    });
  };

  ionOpen = () => {
    this.currentLanguage = this.languageService.currentLanguage;
    this.generateAppPages();
  };

  //helper function for finding the cookie
  getCookie(cname) {
    const name = cname + '=';
    const ca = document.cookie.split(';');
    for (const cookie of ca) {
      let c = cookie;
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  checkSavedFont() {
    const fontSize = this.getCookie('fontSize');
    if (fontSize !== '') {
      return parseInt(fontSize, 10);
    }
    //default to smallest font size
    return 16;
  }
}
